import {Component} from '@angular/core';
import {Button, ButtonDirective} from "primeng/button";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {DynamicDialogConfig, DynamicDialogRef} from "primeng/dynamicdialog";
import {OldMapComponent} from '@components/old-map/old-map.component';
import {IPolygonColor} from '@components/request-map/request-map.component2';
import {FormsModule} from '@angular/forms';
import {CheckboxComponent} from '@components/checkbox';
import {FormBuilder, IMsfFormBuilder} from '@components/form-builder/form-builder';
import {IFieldSources} from '@models/IFieldSource';
import {ConfirmationService, MessageService} from 'primeng/api';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ToastModule} from 'primeng/toast';
import {FieldService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {olCalculateAreaFromPolygon} from '@app/utils/polygons.utils';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {SplitPolygonItem} from '@components/old-map/managers';

declare var ol: any;

@Component({
  selector: 'app-modal-form-with-map',
  standalone: true,
  templateUrl: './modal-edit-field-polygon.html',
  imports: [
    Button,
    ButtonDirective,
    TranslatePipe,
    OldMapComponent,
    FormsModule,
    CheckboxComponent,
    FormBuilder,
    ConfirmDialogModule,
    ToastModule,
  ],
  providers: [ConfirmationService, MessageService],
  styleUrl: './modal-edit-field-polygon.scss'
})
export class ModalEditFieldPolygon {
  formData: any = {};
  multiPolygonJson: IPolygonColor[] = [];
  splitPolygons: SplitPolygonItem[] = [];

  isLoading = false;
  showCrops: boolean = false

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public fieldService: FieldService,
    public confirmationService: ConfirmationService,
    public translate: TranslateService,
    public messageService: MessageService,
  ) {
    this.formData = config.data.model;
    this.multiPolygonJson = config.data.multiPolygonJson || [];
  }

  get canSplit(): boolean {
    return !this.config.data.model?.cadastralNumber?.endsWith('/2');
  }

  save(): void {
    const hasSplit = this.splitPolygons && this.splitPolygons.length === 2;

    const oldSplit = hasSplit ? this.splitPolygons.find(s => s.id === 'old') : null;
    const newSplit = hasSplit ? this.splitPolygons.find(s => s.id === 'new') : null;
    const finalPolygonJson = oldSplit
      ? oldSplit.polygonJson
      : (this.config.data.model.polygon || this.formData?.polygonJson);

    // Build final data for the "old" (existing) field
    const finalData: any = {
      ...this.formData,
      polygonJson: finalPolygonJson
    };

    if (oldSplit) {
      finalData.fieldArea = oldSplit.area;
    } else if (finalPolygonJson) {
      finalData.fieldArea = this.calculateAreaFromPolygon(finalPolygonJson) || finalData.fieldArea || null;
    }
    delete finalData.polygon;

    this.isLoading = true;
    const model = this.config.data;
    if (model.service && model.serviceMethod) {
      model.service[model.serviceMethod](finalData).subscribe({
        next: (savedOld: any) => {
          if (!newSplit) {
            // No split — done
            this.isLoading = false;
            this.ref.close({ success: true, data: savedOld });
            return;
          }


          // Create new field from old data, with '/2' suffix on cadastralNumber and name
          const newFieldData: any = {
            ...this.formData,
            id: undefined,
            polygonJson: newSplit.polygonJson,
            fieldArea: newSplit.area,
            cadastralNumber: this.formData.cadastralNumber ? `${this.formData.cadastralNumber}/2` : undefined,
            name: this.formData.name ? `${this.formData.name}/2` : undefined,
          };
          delete newFieldData.polygon;

          this.fieldService.cropMonitoringFieldCreatePost(newFieldData).subscribe({
            next: (savedNew: any) => {
              this.isLoading = false;

              const newFieldId = savedNew?.id;
              const newFieldName = savedNew?.name ?? `${this.formData.name}/2`;
              const link = `/electronic-field-map/fields/${newFieldId}/general`;

              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('Field created'),
                detail: `${newFieldName} — <a href="${link}" style="color:#fff;font-weight:bold;text-decoration:underline">${this.translate.instant('Open field')}</a>`,
                life: 15000,
                sticky: false,
              });

              setTimeout(() => {
                this.ref.close({ success: true, data: [savedOld, savedNew], split: true });
              }, 200);
            },
            error: () => {
              this.isLoading = false;
            }
          });
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }

  }


getEditSourceForm():IMsfFormBuilder[] {
    return [
      {
        name: 'source',
        title: '',
        type: 'select',
        placeholder: 'Select source',
        selectOptions: IFieldSources,
        options: {
          clearable: false,
          select: {optionLabel: 'name', translateBy: 'locale'},
          col: 'col-6'
        }
      },
      {
        name:"contourConfirmationDate",
        title:"",
        placeholder:"Date of contour confirmation",
        type:"date",
        options:{
          col: 'col-6'
        }
      }
    ]
}
  cancel(): void {
    this.ref.close();
  }

  onMarkerClick($event: { lat: number; lng: number }) {

  }

  afterSplit(polygons: string[]) {
    if (!polygons || polygons.length !== 2) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('FIELD_SPLIT_CONFIRM_MESSAGE'),
      header: this.translate.instant('FIELD_SPLIT_CONFIRM_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.processSplit(polygons);
      },
      reject: () => {
        console.log('Split operation cancelled');
      },
    });
  }

  private processSplit(polygons: string[]) {
    const [polygon1, polygon2] = polygons;

    this.isLoading = true;

    const base = { ...this.formData };
    delete base.polygon;

    const field1 = {
      ...base,
      id: undefined,
      polygonJson: polygon1,
      fieldArea: this.calculateAreaFromPolygon(polygon1) || null,
    };

    const field2 = {
      ...base,
      id: undefined,
      polygonJson: polygon2,
      fieldArea: this.calculateAreaFromPolygon(polygon2) || null,
    };

    this.fieldService.cropMonitoringFieldCreatePost(field1).subscribe({
      next: (response1) => {
        this.fieldService.cropMonitoringFieldCreatePost(field2).subscribe({
          next: (response2) => {
            this.isLoading = false;
            this.ref.close({
              success: true,
              data: [response1, response2],
              split: true,
            });
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error creating second field:', error);
          },
        });
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error creating first field:', error);
      },
    });
  }

  private calculateAreaFromPolygon(polygonWkt: string): number {
    const coords = PolygonConverterUtil.polygonJsonToCoordinates(polygonWkt);
    return olCalculateAreaFromPolygon(JSON.stringify(coords), ol) as any;
  }
}
